import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { useAppDispatch, RootState } from "../../../store";
import {
  fetchAvailablePlans,
  fetchMyAccess,
  fetchLicenseStatus,
  createSubscriptionCheckout,
  changePlan,
  addAddon,
  openBillingPortal,
  cancelSubscription,
  assignTrialLicense,
  syncSubscription,
  fetchPaymentMethods,
  fetchInvoices,
  updateSeats,
  fetchAddonAssignments,
  assignUserToAddon,
  revokeUserFromAddon,
  requestFeatureFromAdmin,
  PaymentMethod,
  Invoice,
  LicensePlan,
  OrgLicenseAccess,
  LicenseStatus,
  AddonAssignment,
  OrgMember,
  AssignmentsResponse,
} from "../../../store/actions/PlanActions";
import { showFeedbackModal } from "../../../store/actions/UserFeedbackActions";
import PlanCard from "./PlanCard";
import AddOnCard from "./AddOnCard";
import CurrentPlanBanner from "./CurrentPlanBanner";
import BillingCard from "./BillingCard";
import InvoicesModal from "./InvoicesModal";
import AddSeatsModal from "./AddSeatsModal";
import AddonAssignmentModal from "./AddonAssignmentModal";
import AddAddonModal from "./AddAddonModal";
import FeatureRequestsTab from "./FeatureRequestsTab";
import TrailUsageBanner from "./TrailUsageBanner";
import Button from "../TailwindComponents/Button";
import Badge from "../TailwindComponents/Badge";
import EmptyState from "../TailwindComponents/EmptyState";
import Skeleton from "../TailwindComponents/Skeleton";
import { FIELD_LABEL_CLASS } from "../TailwindComponents/FieldShell";
import FormSection from "../FormComponents/FormSection";
import { Well } from "../brand/Field";
import clsx from "clsx";
import { Dialog, Transition } from "@headlessui/react";
import {
  PiWarningLight,
  PiXLight,
  PiShieldCheckLight,
  PiRocketLaunchLight,
  PiCrownLight,
  PiPackageLight,
  PiPuzzlePieceLight,
  PiSparkleLight,
  PiFileTextLight,
  PiFunnelLight,
  PiChartBarLight,
  PiBookOpenLight,
  PiHardDriveLight,
  PiDatabaseLight,
  PiClockCountdownLight,
  PiArrowRightLight,
  PiEnvelopeSimpleLight,
} from "react-icons/pi";
import { showSidePanel } from "../../../store/actions/sidePanelActions";
import { useFeatureTour } from '../../../hooks/useFeatureTour';
import { FeatureTour, plansBillingTourSteps } from '../FeatureTour';
import { UPGRADE_PATH } from "../../../config/entitlementKeys";

interface PlansPageProps {
  /**
   * Mounted inside the settings shell, which already draws the page header and
   * owns the scroller. Drops this page's own padding/scroll so the section
   * cards sit at the same rhythm as every other settings section.
   */
  embedded?: boolean;
}

const PlansPage: React.FC<PlansPageProps> = ({ embedded = false }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const history = useHistory();
  const userInfo = useSelector((state: RootState) => state.user.userInfo);
  const plansRef = useRef<HTMLDivElement>(null);

  const { isRunning: isTourRunning, handleJoyrideCallback: handleTourCallback } = useFeatureTour({
    tourId: 'plans-billing',
    steps: plansBillingTourSteps,
    autoStartDelay: 600,
  });

  /* ─── State ─── */
  const [plans, setPlans] = useState<LicensePlan[]>([]);
  const [addons, setAddons] = useState<LicensePlan[]>([]);
  const [access, setAccess] = useState<OrgLicenseAccess | null>(null);
  const [status, setStatus] = useState<LicenseStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [billingPeriod, setBillingPeriod] = useState<"monthly" | "annual">("monthly");
  const [seatCount, setSeatCount] = useState(1);
  const [checkoutLoading, setCheckoutLoading] = useState<number | null>(null);
  const [addonLoading, setAddonLoading] = useState<number | null>(null);
  const [portalLoading, setPortalLoading] = useState(false);
  const [cancelModal, setCancelModal] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [invoicesModal, setInvoicesModal] = useState(false);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);

  /* Seat management */
  const [seatsModal, setSeatsModal] = useState(false);
  const [seatMembers, setSeatMembers] = useState<OrgMember[]>([]);

  /* Addon assignment */
  const [assignmentModal, setAssignmentModal] = useState(false);
  const [assignmentAddon, setAssignmentAddon] = useState<AddonAssignment | null>(null);
  const [assignmentMembers, setAssignmentMembers] = useState<OrgMember[]>([]);
  const [assignmentsData, setAssignmentsData] = useState<AssignmentsResponse | null>(null);

  /* Add addon purchase */
  const [addAddonModal, setAddAddonModal] = useState(false);
  const [addAddonTarget, setAddAddonTarget] = useState<LicensePlan | null>(null);

  /* Plan change confirmation */
  const [changePlanModal, setChangePlanModal] = useState(false);
  const [changePlanTarget, setChangePlanTarget] = useState<LicensePlan | null>(null);
  const [changePlanLoading, setChangePlanLoading] = useState(false);

  /* Subtab: "plans" (default) or "requests" (admin-only) */
  const [subtab, setSubtab] = useState<"plans" | "requests">("plans");

  /* ─── Derived: admin status ─── */
  const isOrgAdmin = userInfo?.is_org_admin === true;

  /* ─── Read subtab from URL (for notification deep-links) ─── */
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlSubtab = params.get("subtab");
    if (urlSubtab === "requests" && isOrgAdmin) {
      setSubtab("requests");
    }
  }, [isOrgAdmin]);

  /* ─── Load data ─── */
  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [plansData, accessData, statusData] = await Promise.all([
        dispatch(fetchAvailablePlans()),
        dispatch(fetchMyAccess()),
        dispatch(fetchLicenseStatus()),
      ]);
      console.log("[PlansPage] plansData:", plansData);
      console.log("[PlansPage] accessData:", accessData);
      console.log("[PlansPage] statusData:", statusData);

      if (plansData) {
        setPlans(plansData.plans || []);
        setAddons(plansData.addons || []);
      }
      if (accessData) setAccess(accessData);
      if (statusData) setStatus(statusData);

      // Fetch payment methods if org has any active license (including trial — they may have added a card via portal)
      if (statusData?.has_active_license) {
        const orgIdVal = userInfo?.OrganizationID || userInfo?.organization_id;
        if (orgIdVal) {
          const pms = await dispatch(fetchPaymentMethods());
          if (pms) setPaymentMethods(pms);
        }

        // Pre-fetch addon assignments so we can show seat usage on cards
        const assignData = await dispatch(fetchAddonAssignments());
        if (assignData) setAssignmentsData(assignData);
      }

      // Auto-assign trial if org has no active license
      if (statusData && !statusData.has_active_license) {
        console.log("[PlansPage] No active license — assigning trial...");
        const trialResult = await dispatch(assignTrialLicense());
        console.log("[PlansPage] Trial assignment result:", trialResult);
        if (trialResult?.success) {
          const [newAccess, newStatus] = await Promise.all([
            dispatch(fetchMyAccess()),
            dispatch(fetchLicenseStatus()),
          ]);
          if (newAccess) setAccess(newAccess);
          if (newStatus) setStatus(newStatus);
        }
      }
    } catch (err) {
      console.error("[PlansPage] loadAll error:", err);
    }
    setLoading(false);
  }, [dispatch]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  /* ─── Derived ─── */
  const orgId = userInfo?.OrganizationID || userInfo?.organization_id;
  const userEmail = userInfo?.Email || userInfo?.email || "";
  const priceCurrency = access?.price_currency ?? null;
  const isBundle = access?.billing_model === "bundle";
  const isUsage = access?.billing_model === "usage";

  const currentBasePlanName = useMemo(() => {
    if (access?.license_info?.plan_name) return access.license_info.plan_name;
    if (status?.base_plan) return status.base_plan;
    return null;
  }, [access, status]);

  const activeAddonNames = useMemo(() => {
    const licInfo = access?.license_info;
    // Non-admin users should only see addons assigned to them, not all org addons
    const names = (!isOrgAdmin && licInfo?.user_addons)
      ? licInfo.user_addons
      : (licInfo?.active_addons || status?.active_addons || []);
    return new Set(names);
  }, [access, status, isOrgAdmin]);

  const currentPlanSortOrder = useMemo(() => {
    if (!currentBasePlanName) return undefined;
    const match = plans.find((p) => p.name === currentBasePlanName);
    return match?.sort_order;
  }, [currentBasePlanName, plans]);

  const currentBasePlan = useMemo(() => {
    if (!currentBasePlanName) return null;
    return plans.find((p) => p.name === currentBasePlanName) || null;
  }, [currentBasePlanName, plans]);

  /** Map addon license_id → assignment data for seat usage display */
  const addonAssignmentMap = useMemo(() => {
    const map = new Map<number, { assignedCount: number; totalSeats: number }>();
    if (assignmentsData?.addons) {
      for (const a of assignmentsData.addons) {
        map.set(a.license_id, { assignedCount: a.assigned_count, totalSeats: a.quantity });
      }
    }
    return map;
  }, [assignmentsData]);

  /** Full-plan value nudge: suggest upgrading when add-on costs approach the Full plan price */
  const fullPlanNudge = useMemo(() => {
    const fullPlan = plans.find((p) => p.type === "base" && (p.name || "").toLowerCase().includes("full"));
    if (!fullPlan) return null;
    // Don't nudge if already on Full plan or higher
    if (currentBasePlanName && currentBasePlanName.toLowerCase().includes("full")) return null;
    // Count active add-ons cost
    const activeAddonPlans = addons.filter(
      (a) => activeAddonNames.has(a.display_name) || activeAddonNames.has(a.name)
    );
    if (activeAddonPlans.length < 2) return null; // Only nudge with 2+ add-ons
    const addonTotal =
      billingPeriod === "monthly"
        ? activeAddonPlans.reduce((sum, a) => sum + a.price_monthly, 0)
        : activeAddonPlans.reduce((sum, a) => sum + a.price_annual, 0);
    const baseCost = currentBasePlan
      ? billingPeriod === "monthly"
        ? currentBasePlan.price_monthly
        : currentBasePlan.price_annual
      : 0;
    const currentTotal = baseCost + addonTotal;
    const fullPrice = billingPeriod === "monthly" ? fullPlan.price_monthly : fullPlan.price_annual;
    // Only nudge if Full plan is cheaper or within 20% of what they're already paying
    if (fullPrice > currentTotal * 1.2) return null;
    const savings = currentTotal - fullPrice;
    return { fullPlan, currentTotal, fullPrice, savings, addonCount: activeAddonPlans.length };
  }, [plans, addons, activeAddonNames, currentBasePlanName, currentBasePlan, billingPeriod]);

  /* ─── Handlers ─── */
  const hasActiveStripeSubscription = useMemo(() => {
    return access?.licenses?.some((l) => l.stripe_subscription_id && l.is_active) ?? false;
  }, [access]);

  const handleSelectPlan = async (plan: LicensePlan) => {
    if (!orgId) {
      dispatch(showFeedbackModal({ showModal: true, message: t("Organization not found"), modalType: "error", duration: 3000 }));
      return;
    }
    // Non-admin users can only request upgrades
    if (!isOrgAdmin) {
      const sectionLabel = plan.display_name || plan.name;
      dispatch(requestFeatureFromAdmin(sectionLabel, t("I'd like to upgrade to the {{plan}} plan.", { plan: sectionLabel })));
      return;
    }
    // If org already has an active Stripe subscription, modify it instead of creating a new one
    if (hasActiveStripeSubscription) {
      setChangePlanTarget(plan);
      setChangePlanModal(true);
      return;
    }
    // No subscription yet — go through Stripe Checkout
    setCheckoutLoading(plan.id);
    try {
      const successUrl = `${window.location.origin}/plans?success=true`;
      const cancelUrl = `${window.location.origin}/plans?canceled=true`;
      const result = await dispatch(
        createSubscriptionCheckout({
          license_ids: [plan.id],
          email: userEmail,
          billing_period: billingPeriod,
          seat_count: seatCount,
          success_url: successUrl,
          cancel_url: cancelUrl,
        })
      );
      if (result?.url) {
        window.location.assign(result.url);
      }
    } finally {
      setCheckoutLoading(null);
    }
  };

  const handleConfirmChangePlan = async () => {
    if (!orgId || !changePlanTarget) return;
    setChangePlanLoading(true);
    try {
      const result = await dispatch(
        changePlan({
          new_license_id: changePlanTarget.id,
          billing_period: billingPeriod,
        })
      );
      if (result) {
        setChangePlanModal(false);
        setChangePlanTarget(null);
        await loadAll();
      }
    } finally {
      setChangePlanLoading(false);
    }
  };

  const handleAddAddon = async (addon: LicensePlan) => {
    // Non-admin users can only request addons
    if (!isOrgAdmin) {
      const label = addon.display_name || addon.name;
      dispatch(requestFeatureFromAdmin(label, t("I'd like to add the {{addon}} add-on.", { addon: label })));
      return;
    }
    setAddAddonTarget(addon);
    setAddAddonModal(true);
  };

  const handleConfirmAddAddon = async (
    addon: LicensePlan,
    seatCount: number,
    applyTeamDiscount: boolean
  ) => {
    if (!orgId) return;
    setAddonLoading(addon.id);
    try {
      const successUrl = `${window.location.origin}/plans?success=true`;
      const cancelUrl = `${window.location.origin}/plans?canceled=true`;
      const result = await dispatch(
        addAddon({
          addon_license_id: addon.id,
          billing_period: billingPeriod,
          seat_count: seatCount,
          apply_team_discount: applyTeamDiscount,
          email: userEmail,
          success_url: successUrl,
          cancel_url: cancelUrl,
        })
      );
      if (result?.requires_checkout && result?.url) {
        window.location.assign(result.url);
        return;
      }
      if (result) await loadAll();
    } finally {
      setAddonLoading(null);
    }
  };

  const handleManageBilling = async () => {
    if (!orgId) return;
    setPortalLoading(true);
    try {
      const result = await dispatch(
        openBillingPortal({
          return_url: window.location.href,
        })
      );
      if (result?.url) {
        window.location.assign(result.url);
      }
    } finally {
      setPortalLoading(false);
    }
  };

  const handleOpenInvoices = async () => {
    if (!orgId) return;
    setInvoicesModal(true);
    setInvoicesLoading(true);
    try {
      const result = await dispatch(fetchInvoices());
      setInvoices(result || []);
    } finally {
      setInvoicesLoading(false);
    }
  };

  /* ── Seat Management ── */
  const handleOpenSeatsModal = async () => {
    // Fetch members so we can show who occupies seats
    const data = await dispatch(fetchAddonAssignments());
    if (data?.members) {
      setSeatMembers(data.members);
    }
    setSeatsModal(true);
  };

  const handleUpdateSeats = async (newQuantity: number) => {
    if (!orgId || !access) {
      dispatch(showFeedbackModal({ showModal: true, message: t("Organization data not loaded"), modalType: "error", duration: 3000 }));
      throw new Error("no_org");
    }
    // Find the base plan's org_license_id
    const baseLicense = access.licenses.find((l) => ["base", "trial", "pilot"].includes(l.type));
    if (!baseLicense) {
      dispatch(showFeedbackModal({ showModal: true, message: t("No active base license found"), modalType: "error", duration: 3000 }));
      throw new Error("no_license");
    }
    const result = await dispatch(
      updateSeats({
        org_license_id: baseLicense.id,
        new_quantity: newQuantity,
      })
    );
    if (result) {
      await loadAll();
    } else {
      throw new Error("update_failed");
    }
  };

  /* ── Addon Assignment ── */
  const handleManageAssignments = async (addon: LicensePlan) => {
    // Fetch assignment data, then open modal for the matching addon
    const data = await dispatch(fetchAddonAssignments());
    if (!data) return;
    setAssignmentsData(data);
    setAssignmentMembers(data.members || []);
    const match = data.addons.find((a: AddonAssignment) => a.license_id === addon.id);
    if (match) {
      setAssignmentAddon(match);
      setAssignmentModal(true);
    }
  };

  const handleManageBasePlanSeats = async () => {
    const data = await dispatch(fetchAddonAssignments());
    if (!data) return;
    setAssignmentsData(data);
    setAssignmentMembers(data.members || []);
    if (data.base_plan) {
      setAssignmentAddon(data.base_plan);
      setAssignmentModal(true);
    }
  };

  const handleAssignUser = async (orgLicenseId: number, userId: number): Promise<boolean> => {
    const result = await dispatch(assignUserToAddon({ org_license_id: orgLicenseId, user_id: userId }));
    return !!result;
  };

  const handleRevokeUser = async (orgLicenseId: number, userId: number): Promise<boolean> => {
    const result = await dispatch(revokeUserFromAddon({ org_license_id: orgLicenseId, user_id: userId }));
    return !!result;
  };

  const handleCancelSubscription = async () => {
    if (!orgId) return;
    setCancelLoading(true);
    try {
      const result = await dispatch(
        cancelSubscription({ at_period_end: true })
      );
      if (result) {
        setCancelModal(false);
        await loadAll();
      }
    } finally {
      setCancelLoading(false);
    }
  };

  const scrollToPlans = () => {
    plansRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleViewDetails = (plan: LicensePlan) => {
    const isBase = plan.type === "base";
    const isCurrent = isBase ? currentBasePlanName === plan.name : false;
    const isActiveAddon = !isBase && (activeAddonNames.has(plan.display_name) || activeAddonNames.has(plan.name));

    // Pick the right header icon
    const getHeaderIcon = () => {
      if (isBase) {
        const key = (plan.name || plan.display_name).toLowerCase();
        if (key.includes("starter")) return <PiShieldCheckLight className="h-5 w-5 text-fg-strong" />;
        if (key.includes("professional")) return <PiRocketLaunchLight className="h-5 w-5 text-fg-strong" />;
        if (key.includes("enterprise")) return <PiCrownLight className="h-5 w-5 text-fg-strong" />;
        return <PiPackageLight className="h-5 w-5 text-fg-strong" />;
      }
      const name = (plan.name || plan.display_name).toLowerCase();
      const sections = plan.feature_gates?.sections || [];
      const ai = plan.feature_gates?.ai_features || [];
      if (ai.length > 0 || name.includes("ai") || sections.some((s: string) => ["research", "tables"].includes(s)))
        return <PiSparkleLight className="h-5 w-5 text-fg-strong" />;
      if (name.includes("template") || sections.includes("templates")) return <PiFileTextLight className="h-5 w-5 text-fg-strong" />;
      if (name.includes("pipeline") || sections.includes("client_pipeline")) return <PiFunnelLight className="h-5 w-5 text-fg-strong" />;
      if (name.includes("analytics") || sections.includes("analytics")) return <PiChartBarLight className="h-5 w-5 text-fg-strong" />;
      if (name.includes("knowledge") || sections.includes("data_sources")) return <PiBookOpenLight className="h-5 w-5 text-fg-strong" />;
      return <PiPuzzlePieceLight className="h-5 w-5 text-fg-strong" />;
    };

    dispatch(
      showSidePanel({
        type: "licenseDetail" as any,
        showSidePanel: true,
        width: "40%",
        message: isBase ? t("Base License") : t("Add-on"),
        icon: getHeaderIcon(),
        plan,
        billingPeriod,
        isCurrentPlan: isCurrent,
        isActive: isActiveAddon,
        currentPlanSortOrder,
        seatCount: isCurrent ? (access?.license_info?.seat_count || status?.seat_count || 1) : undefined,
        seatsUsed: isCurrent ? (access?.license_info?.seats_used || status?.user_count || 0) : undefined,
        onUpdateSeats: isCurrent ? handleUpdateSeats : undefined,
        onAction: (p: LicensePlan, upgradeSeatCount?: number) => {
          if (isBase) {
            if (upgradeSeatCount && upgradeSeatCount > 1) setSeatCount(upgradeSeatCount);
            handleSelectPlan(p);
          } else {
            handleAddAddon(p);
          }
        },
      } as any)
    );
  };

  /* ─── Success / Canceled query params ─── */
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("success") === "true") {
      // Sync subscription from Stripe into DB, then reload license data.
      // Must wait for orgId — on redirect it may not be hydrated on first render.
      if (orgId) {
        dispatch(syncSubscription() as any).then(() => {
          loadAll();
        });
        dispatch(showFeedbackModal({
          showModal: true,
          message: t("Subscription activated successfully!"),
          modalType: "success",
          duration: 5000,
        }));
        history.replace(UPGRADE_PATH);
      }
      // If orgId isn't ready yet, this effect will re-run when it becomes available
      return;
    }
    if (params.get("canceled") === "true") {
      dispatch(showFeedbackModal({
        showModal: true,
        message: t("Checkout was canceled"),
        modalType: "info" as any,
        duration: 3000,
      }));
      history.replace(UPGRADE_PATH);
    }
  }, [orgId]); // eslint-disable-line react-hooks/exhaustive-deps

  // For non-admins: only show current plan + the one they can upgrade to.
  // Must be before any early returns to preserve hook order.
  const isOnPaidPlan = currentBasePlanName === 'pons_starter' || currentBasePlanName === 'pons_full';
  const isOnFull = currentBasePlanName === 'pons_full';
  const isOnPilot = status?.plan_type === 'pilot';

  const nonAdminVisiblePlans = useMemo(() => {
    return plans
      .filter((p) => p.type === "base" || p.type === "trial")
      .filter((p) => {
        if (p.name === currentBasePlanName) return true;
        if (p.type === "trial" && isOnPaidPlan) return false;
        if (isOnFull || isOnPilot) return false;
        if (currentPlanSortOrder !== undefined && p.sort_order <= currentPlanSortOrder) return false;
        return true;
      })
      .sort((a, b) => a.sort_order - b.sort_order);
  }, [plans, currentBasePlanName, isOnPaidPlan, isOnFull, isOnPilot, currentPlanSortOrder]);

  /* ─── Render ─── */
  if (loading) {
    return <Skeleton variant="card" rows={4} />;
  }

  /**
   * The period switcher and the plans/requests switcher are the same control at
   * two sizes, so they are the same markup: the app's `.nav-pill` treatment in a
   * `.glass-bar`, never a hand-rolled `bg-gray-100` active state.
   */
  const pill = (active: boolean) => clsx('nav-pill px-3 py-1.5 text-xs', active && 'nav-pill-active');

  const periodSwitcher = (
    <div ref={plansRef} className="glass-bar inline-flex items-center gap-1 rounded-lg p-1">
      <button type="button" onClick={() => setBillingPeriod("monthly")} className={pill(billingPeriod === "monthly")}>
        {t("Monthly")}
      </button>
      <button
        type="button"
        onClick={() => setBillingPeriod("annual")}
        className={clsx(pill(billingPeriod === "annual"), 'flex items-center gap-1.5')}
      >
        {t("Annual")}
        <span className="text-success-600 dark:text-success-400">−17%</span>
      </button>
    </div>
  );

  const subtabSwitcher = (
    <div className="glass-bar inline-flex items-center gap-1 rounded-lg p-1">
      <button type="button" onClick={() => setSubtab("plans")} className={pill(subtab === "plans")}>
        {t("Plans")}
      </button>
      <button
        type="button"
        onClick={() => setSubtab("requests")}
        className={clsx(pill(subtab === "requests"), 'flex items-center gap-1.5')}
      >
        <PiEnvelopeSimpleLight className="w-3.5 h-3.5" />
        {t("Requests")}
      </button>
    </div>
  );

  /** A "coming soon" capability tile — a well, like every other inner block. */
  const comingSoon = (icon: React.ReactNode, title: string, body: string) => (
    <Well className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <span className="flex items-center gap-1.5">
          {icon}
          <span className="text-sm font-medium text-fg-body">{title}</span>
        </span>
        <Badge color="slate">{t("Coming Soon")}</Badge>
      </div>
      <p className="text-xs text-fg-muted">{body}</p>
    </Well>
  );

  return (
    <>
    <div className={clsx('space-y-4', !embedded && 'overflow-auto custom-scrollbar-main p-4')}>
      {/* ══════════════════════════════════════════════════════
          NON-ADMIN VIEW — simplified: only their plan + request options
         ══════════════════════════════════════════════════════ */}
      {!isOrgAdmin ? (
      <>
      <FormSection
        icon={<PiPackageLight />}
        title={t("My plan")}
        description={t("Your current plan and available features")}
        noGrid
      >
        <CurrentPlanBanner
          access={access}
          status={status}
          isOrgAdmin={false}
          onChangePlan={scrollToPlans}
        />

        {/* Trail usage for non-admins */}
        {status?.is_trial && <TrailUsageBanner />}

        {/* User's active add-ons */}
        {access?.license_info?.user_addons && access.license_info.user_addons.length > 0 && (
          <div className="space-y-1.5">
            <p className={FIELD_LABEL_CLASS}>{t("Your Add-ons")}</p>
            <div className="flex flex-wrap gap-2">
              {access.license_info.user_addons.map((addonName) => (
                <Badge key={addonName} color="slate" icon={<PiPuzzlePieceLight className="h-3.5 w-3.5" />}>
                  {addonName}
                </Badge>
              ))}
            </div>
          </div>
        )}
      </FormSection>

      {/* Upgrade options — only show if there's something to upgrade to */}
      {nonAdminVisiblePlans.length > 1 && (
        <FormSection
          icon={<PiRocketLaunchLight />}
          title={t("Available plans")}
          description={t("Browse and compare plans to find the right fit")}
          noGrid
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 2xl:grid-cols-3 gap-4 items-stretch">
            {nonAdminVisiblePlans.map((plan) => (
              <PlanCard
                key={plan.id}
                plan={plan}
                billingPeriod={billingPeriod}
                isCurrentPlan={currentBasePlanName === plan.name}
                currentPlanSortOrder={currentPlanSortOrder}
                onSelect={handleSelectPlan}
                isLoading={checkoutLoading === plan.id}
                onViewDetails={handleViewDetails}
                isOrgAdmin={false}
                priceCurrency={priceCurrency}
                isCurrentPlanBundle={isBundle}
              />
            ))}
          </div>
        </FormSection>
      )}

      {/* Add-ons to request (Starter only) */}
      {addons.length > 0 && currentBasePlanName === 'pons_starter' && (
        <FormSection
          icon={<PiPuzzlePieceLight />}
          title={t("Available add-ons")}
          description={t("Request add-ons from your admin to extend your capabilities")}
          noGrid
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 2xl:grid-cols-3 gap-4 items-start">
            {addons
              .sort((a, b) => a.sort_order - b.sort_order)
              .map((addon) => (
                <AddOnCard
                  key={addon.id}
                  addon={addon}
                  billingPeriod={billingPeriod}
                  isActive={activeAddonNames.has(addon.display_name) || activeAddonNames.has(addon.name)}
                  onAdd={handleAddAddon}
                  onViewDetails={handleViewDetails}
                  isOrgAdmin={false}
                  priceCurrency={priceCurrency}
                />
              ))}
          </div>
        </FormSection>
      )}
      </>
      ) : (
      /* ══════════════════════════════════════════════════════
          ADMIN VIEW — full management
         ══════════════════════════════════════════════════════ */
      <>
      {/* The switcher rides the first section's header row rather than a page
          heading of its own — the settings shell already names this page. */}
      {subtab === "requests" ? (
        <FormSection
          icon={<PiEnvelopeSimpleLight />}
          title={t("Feature requests")}
          description={t("What your colleagues have asked you to unlock")}
          actions={subtabSwitcher}
          noGrid
        >
          <FeatureRequestsTab addons={addons} />
        </FormSection>
      ) : (
      <>
      <FormSection
        icon={<PiPackageLight />}
        title={t("Current plan & billing")}
        description={t("Manage your subscription, seats, and billing details")}
        actions={subtabSwitcher}
        noGrid
      >
        <div data-tour-id="plans-current" className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-stretch">
          <CurrentPlanBanner
            access={access}
            status={status}
            isOrgAdmin={true}
            onChangePlan={scrollToPlans}
            onAddSeats={(isBundle || isUsage) ? undefined : handleOpenSeatsModal}
            onManageSeats={handleManageBasePlanSeats}
          />

          <BillingCard
            access={access}
            status={status}
            onManageBilling={handleManageBilling}
            onViewInvoices={handleOpenInvoices}
            isPortalLoading={portalLoading}
            paymentMethods={paymentMethods}
          />
        </div>

        {status?.is_trial && <TrailUsageBanner />}
      </FormSection>

      <FormSection
        icon={<PiRocketLaunchLight />}
        title={t("Available plans")}
        description={t("Browse and compare plans to find the right fit for your organization")}
        actions={periodSwitcher}
        noGrid
      >
        {plans.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 2xl:grid-cols-3 gap-4 items-stretch">
            {plans
              .filter((p) => p.type === "base" || p.type === "trial")
              .sort((a, b) => a.sort_order - b.sort_order)
              .map((plan) => (
                <PlanCard
                  key={plan.id}
                  plan={plan}
                  billingPeriod={billingPeriod}
                  isCurrentPlan={currentBasePlanName === plan.name}
                  currentPlanSortOrder={currentPlanSortOrder}
                  onSelect={handleSelectPlan}
                  isLoading={checkoutLoading === plan.id}
                  onViewDetails={handleViewDetails}
                  isOrgAdmin={isOrgAdmin}
                  priceCurrency={priceCurrency}
                  isCurrentPlanBundle={isBundle}
                />
              ))}
          </div>
        ) : (
          <EmptyState
            density="compact"
            icon={<PiPackageLight />}
            headline={t("No plans available for your organization type")}
          />
        )}
      </FormSection>

      {/* ── Add-Ons — only shown for Starter plan (addon-eligible) ── */}
      {addons.length > 0 && currentBasePlanName === 'pons_starter' && (
        <FormSection
          icon={<PiPuzzlePieceLight />}
          title={t("Add-ons")}
          description={t("Extend your plan with additional capabilities")}
          noGrid
        >
          {/* Full-plan value nudge */}
          {fullPlanNudge && (
            <Well className="flex items-center gap-3">
              <PiCrownLight className="h-5 w-5 text-champagne flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-fg-body">
                  {t("Save with")} {fullPlanNudge.fullPlan.display_name}
                </p>
                <p className="text-xs text-fg-muted">
                  {t("You're paying")} ${fullPlanNudge.currentTotal.toFixed(0)}/{t("mo")} {t("for your plan + {{count}} add-ons", { count: fullPlanNudge.addonCount })}.{" "}
                  {fullPlanNudge.savings > 0
                    ? t("Upgrade to {{plan}} at ${{price}}/mo and save ${{savings}}/mo — everything included.", {
                        plan: fullPlanNudge.fullPlan.display_name,
                        price: fullPlanNudge.fullPrice.toFixed(0),
                        savings: fullPlanNudge.savings.toFixed(0),
                      })
                    : t("Get {{plan}} at ${{price}}/mo with everything included.", {
                        plan: fullPlanNudge.fullPlan.display_name,
                        price: fullPlanNudge.fullPrice.toFixed(0),
                      })}
                </p>
              </div>
              <Button
                variant="primary"
                size="small"
                onClick={() => handleSelectPlan(fullPlanNudge.fullPlan)}
                className="flex-shrink-0"
              >
                {t("Upgrade")}
                <PiArrowRightLight className="w-3.5 h-3.5 ml-1" />
              </Button>
            </Well>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 2xl:grid-cols-3 gap-4 items-start">
            {addons
              .sort((a, b) => a.sort_order - b.sort_order)
              .map((addon) => {
                const addonAssign = addonAssignmentMap.get(addon.id);
                return (
                  <AddOnCard
                    key={addon.id}
                    addon={addon}
                    billingPeriod={billingPeriod}
                    isActive={activeAddonNames.has(addon.display_name) || activeAddonNames.has(addon.name)}
                    onAdd={handleAddAddon}
                    isLoading={addonLoading === addon.id}
                    onViewDetails={handleViewDetails}
                    onManageAssignments={handleManageAssignments}
                    teamSize={access?.license_info?.seat_count || status?.seat_count || 1}
                    assignedCount={addonAssign?.assignedCount}
                    totalSeats={addonAssign?.totalSeats}
                    isOrgAdmin={isOrgAdmin}
                    priceCurrency={priceCurrency}
                  />
                );
              })}
          </div>
        </FormSection>
      )}

      {/* ── Usage & Limits ── */}
      <FormSection
        icon={<PiHardDriveLight />}
        title={t("Usage & limits")}
        description={t("Control storage, data sources, and data retention for your organization")}
        noGrid
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 2xl:grid-cols-3 gap-4 items-start">
          {comingSoon(
            <PiHardDriveLight className="h-4 w-4 text-fg-muted flex-shrink-0" />,
            t("Increase Storage"),
            t("Add more storage capacity for your files, documents, and case data.")
          )}
          {comingSoon(
            <PiDatabaseLight className="h-4 w-4 text-fg-muted flex-shrink-0" />,
            t("More Data Sources"),
            t("Connect additional knowledge bases, external databases, and document repositories.")
          )}
          {comingSoon(
            <PiClockCountdownLight className="h-4 w-4 text-fg-muted flex-shrink-0" />,
            t("Retention Period"),
            t("Configure how long your organization's data is retained. Set retention policies in years to comply with regulatory requirements.")
          )}
        </div>
      </FormSection>

      {/* ── Cancel Subscription ── */}
      {isOrgAdmin && status?.has_active_license && !status.is_trial && (
        <FormSection
          icon={<PiXLight />}
          title={t("Subscription")}
          description={t("Pause or stop your plan")}
          actions={
            <Button variant="destructive" size="small" onClick={() => setCancelModal(true)}>
              {t("Cancel Subscription")}
            </Button>
          }
          noGrid
        >
          <Well>
            <p className="text-sm text-fg-muted">
              {t("Need to pause or stop your subscription? You can cancel anytime — your plan stays active until the end of the billing period.")}
            </p>
          </Well>
        </FormSection>
      )}
      </>
      )}
      </>
      )}

      {/* ── Cancel Modal ── */}
      <Transition show={cancelModal} as={React.Fragment}>
        <Dialog onClose={() => setCancelModal(false)} className="relative z-50">
          <Transition.Child
            as={React.Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-ink/20 backdrop-blur-[2px]" />
          </Transition.Child>

          <div className="fixed inset-0 flex items-center justify-center p-4">
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="glass w-full max-w-md rounded-lg shadow-lg p-4">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <Dialog.Title className="font-medium text-fg-strong text-sm">
                      {t("Cancel Subscription?")}
                    </Dialog.Title>
                    <p className="text-xs text-fg-muted mt-0.5">
                      {t("This action cannot be undone")}
                    </p>
                  </div>
                  <button
                    onClick={() => setCancelModal(false)}
                    className="p-1 rounded hover:bg-surface-sunken"
                  >
                    <PiXLight className="h-4 w-4 text-fg-muted" />
                  </button>
                </div>

                <div className="rounded-lg border border-warning-200 dark:border-warning-800 bg-warning-50 dark:bg-warning-900/20 p-3 mb-4 text-xs text-warning-800 dark:text-warning-300">
                  {t(
                    "Your access will continue until the end of your current billing period. After that, your organization will lose access to licensed features."
                  )}
                </div>

                <div className="flex justify-end gap-2">
                  <Button
                    variant="neutral"
                    size="small"
                    onClick={() => setCancelModal(false)}
                  >
                    {t("Keep Subscription")}
                  </Button>
                  <Button
                    variant="destructive"
                    size="small"
                    onClick={handleCancelSubscription}
                    isLoading={cancelLoading}
                  >
                    {t("Cancel Subscription")}
                  </Button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>

      {/* ── Invoices Modal ── */}
      <InvoicesModal
        open={invoicesModal}
        onClose={() => setInvoicesModal(false)}
        invoices={invoices}
        loading={invoicesLoading}
      />

      {/* ── Change Plan Confirmation Modal ── */}
      <Transition show={changePlanModal} as={React.Fragment}>
        <Dialog onClose={() => { setChangePlanModal(false); setChangePlanTarget(null); }} className="relative z-50">
          <Transition.Child
            as={React.Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-ink/20 backdrop-blur-[2px]" />
          </Transition.Child>

          <div className="fixed inset-0 flex items-center justify-center p-4">
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="glass w-full max-w-md rounded-lg shadow-lg p-4">
                {changePlanTarget && (() => {
                  const isDowngrade = currentPlanSortOrder !== undefined && changePlanTarget.sort_order < currentPlanSortOrder;
                  const newPrice = billingPeriod === "monthly" ? changePlanTarget.price_monthly : changePlanTarget.price_annual;
                  const currentPrice = currentBasePlan
                    ? (billingPeriod === "monthly" ? currentBasePlan.price_monthly : currentBasePlan.price_annual)
                    : 0;

                  // Identify features being lost on downgrade
                  const currentSections = new Set(currentBasePlan?.feature_gates?.sections || []);
                  const newSections = new Set(changePlanTarget.feature_gates?.sections || []);
                  const lostSections = [...currentSections].filter((s) => !newSections.has(s));
                  const currentAi = new Set(currentBasePlan?.feature_gates?.ai_features || []);
                  const newAi = new Set(changePlanTarget.feature_gates?.ai_features || []);
                  const lostAi = [...currentAi].filter((f) => !newAi.has(f));

                  return (
                    <>
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <Dialog.Title className="font-medium text-fg-strong text-sm">
                            {isDowngrade ? t("Downgrade to {{plan}}?", { plan: changePlanTarget.display_name }) : t("Upgrade to {{plan}}?", { plan: changePlanTarget.display_name })}
                          </Dialog.Title>
                          <p className="text-xs text-fg-muted mt-0.5">
                            {isDowngrade
                              ? t("Your plan will change at the end of the current billing period")
                              : t("You'll be charged the prorated difference immediately")}
                          </p>
                        </div>
                        <button
                          onClick={() => { setChangePlanModal(false); setChangePlanTarget(null); }}
                          className="p-1 rounded hover:bg-surface-sunken"
                        >
                          <PiXLight className="h-4 w-4 text-fg-muted" />
                        </button>
                      </div>

                      {/* Price comparison */}
                      <div className="rounded-lg border border-hairline bg-surface-sunken/70 p-3 mb-3 text-xs space-y-1">
                        <div className="flex justify-between">
                          <span className="text-fg-muted">{t("Current plan")}</span>
                          <span className="font-medium text-fg-strong">${currentPrice.toFixed(0)}/{t("mo")}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-fg-muted">{t("New plan")}</span>
                          <span className="font-medium text-fg-strong">${newPrice.toFixed(0)}/{t("mo")}</span>
                        </div>
                      </div>

                      {/* Feature loss warning for downgrades */}
                      {isDowngrade && (lostSections.length > 0 || lostAi.length > 0) && (
                        <div className="rounded-lg border border-warning-200 dark:border-warning-800 bg-warning-50 dark:bg-warning-900/20 p-3 mb-3 text-xs">
                          <div className="flex items-start gap-2">
                            <PiWarningLight className="h-4 w-4 text-warning-600 dark:text-warning-400 flex-shrink-0 mt-0.5" />
                            <div>
                              <p className="font-medium text-warning-800 dark:text-warning-300 mb-1">
                                {t("You will lose access to the following features:")}
                              </p>
                              <ul className="space-y-0.5 text-warning-700 dark:text-warning-400">
                                {lostSections.map((s) => (
                                  <li key={s}>• {s.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())}</li>
                                ))}
                                {lostAi.map((f) => (
                                  <li key={f}>• AI: {f.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())}</li>
                                ))}
                              </ul>
                            </div>
                          </div>
                        </div>
                      )}

                      <div className="flex justify-end gap-2">
                        <Button
                          variant="neutral"
                          size="small"
                          onClick={() => { setChangePlanModal(false); setChangePlanTarget(null); }}
                        >
                          {t("Cancel")}
                        </Button>
                        <Button
                          variant={isDowngrade ? "outline" : "primary"}
                          size="small"
                          onClick={handleConfirmChangePlan}
                          isLoading={changePlanLoading}
                        >
                          {isDowngrade ? t("Confirm Downgrade") : t("Confirm Upgrade")}
                        </Button>
                      </div>
                    </>
                  );
                })()}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>

      {/* Add Seats Modal removed — seats are now assigned directly, billing adjusts automatically */}

      {/* ── Addon Assignment Modal ── */}
      <AddonAssignmentModal
        open={assignmentModal}
        onClose={async () => {
          setAssignmentModal(false);
          setAssignmentAddon(null);
          // Refresh assignments data so card seat counts update
          const data = await dispatch(fetchAddonAssignments());
          if (data) setAssignmentsData(data);
        }}
        addon={assignmentAddon}
        members={assignmentMembers}
        onAssign={handleAssignUser}
        onRevoke={handleRevokeUser}
      />

      {/* ── Add Addon Purchase Modal ── */}
      <AddAddonModal
        open={addAddonModal}
        onClose={() => { setAddAddonModal(false); setAddAddonTarget(null); }}
        addon={addAddonTarget}
        billingPeriod={billingPeriod}
        teamSize={access?.license_info?.seat_count || status?.seat_count || 1}
        onConfirm={handleConfirmAddAddon}
        fullPlanSuggestion={(() => {
          const fullPlan = plans.find((p) => p.type === "base" && (p.name || "").toLowerCase().includes("full"));
          if (!fullPlan || !addAddonTarget) return null;
          if (currentBasePlanName && currentBasePlanName.toLowerCase().includes("full")) return null;
          const activeAddonCost = addons
            .filter((a) => activeAddonNames.has(a.display_name) || activeAddonNames.has(a.name))
            .reduce((sum, a) => sum + (billingPeriod === "monthly" ? a.price_monthly : a.price_annual), 0);
          const baseCost = currentBasePlan ? (billingPeriod === "monthly" ? currentBasePlan.price_monthly : currentBasePlan.price_annual) : 0;
          const newAddonCost = billingPeriod === "monthly" ? addAddonTarget.price_monthly : addAddonTarget.price_annual;
          return {
            fullPlan,
            fullPlanPrice: billingPeriod === "monthly" ? fullPlan.price_monthly : fullPlan.price_annual,
            currentTotalWithAddon: baseCost + activeAddonCost + newAddonCost,
          };
        })()}
        onUpgradeToFull={handleSelectPlan}
      />
    </div>
    <FeatureTour
      steps={plansBillingTourSteps}
      run={isTourRunning}
      onCallback={handleTourCallback}
      disableOverlay
    />
    </>
  );
};

export default PlansPage;
